import * as styles from "./AmendmentList.css";

import { Provider, createStore, useSetAtom } from "jotai";
import { useEffect } from "react";
import type { JSX } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderHeading,
} from "@frontend/components/organisms/PageHeader";
import { Trans, useLingui } from "@lingui/react/macro";

import { AmendmentEmployeesTab } from "./components/AmendmentEmployeesTab";
import { AmendmentSubscriptionsTab } from "./components/AmendmentSubscriptionsTab";
import { setAmendmentEmployeesYearAtom } from "./stores/employees";
import { setAmendmentYearAtom } from "./stores/subscriptions";

export type AmendmentListProps = {
  /** The year the list opens on, normally the current one. */
  year: number;
};

/** Three years back and one ahead: closure weeks are prepared a year early. */
function yearChoices(year: number): number[] {
  return [year + 1, year, year - 1, year - 2, year - 3];
}

function AmendmentListBody({ year }: AmendmentListProps): JSX.Element {
  const { t } = useLingui();

  // The two tabs read the same year, each from its own store.
  const setSubscriptionsYear = useSetAtom(setAmendmentYearAtom);
  const setEmployeesYear = useSetAtom(setAmendmentEmployeesYearAtom);

  useEffect(() => {
    setSubscriptionsYear(year);
    setEmployeesYear(year);
  }, [setSubscriptionsYear, setEmployeesYear, year]);

  return (
    <div className={styles.root}>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderHeading>
            <Trans>Amendments</Trans>
          </PageHeaderHeading>
        </PageHeaderContent>

        <PageHeaderActions>
          <Select
            items={yearChoices(year).map((choice) => ({
              label: String(choice),
              value: String(choice),
            }))}
            defaultValue={String(year)}
            onValueChange={(next) => {
              setSubscriptionsYear(Number(next));
              setEmployeesYear(Number(next));
            }}
          >
            <SelectTrigger className={styles.year} aria-label={t`Year`}>
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {yearChoices(year).map((choice) => (
                <SelectItem key={choice} value={String(choice)}>
                  {choice}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PageHeaderActions>
      </PageHeader>

      {/* The collaborateurs first: that is where the amendments are generated,
          the subscriptions tab being the state of their closure weeks. */}
      <Tabs defaultValue="employees" variant="underline">
        <TabsList>
          <TabsTab value="employees" icon="Users">
            <Trans>Collaborateurs</Trans>
          </TabsTab>

          <TabsTab value="subscriptions" icon="Building2">
            <Trans>Subscriptions</Trans>
          </TabsTab>

          <TabsIndicator />
        </TabsList>

        <TabsContent>
          <TabsPanel value="employees">
            <AmendmentEmployeesTab />
          </TabsPanel>

          <TabsPanel value="subscriptions">
            <AmendmentSubscriptionsTab />
          </TabsPanel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function AmendmentList(props: AmendmentListProps): JSX.Element {
  // A store of its own: two lists on one page keep their own year and
  // their own filters.
  return (
    <Provider store={createStore()}>
      <AmendmentListBody {...props} />
    </Provider>
  );
}
